import React from 'react';
import Head from 'next/head';
import getConfig from 'next/config';

import {normalizeDate, utcToIso8601} from '~/src/utilities/helpers/date';

import {ImageVariant} from '~/src/types/data/common';
import {Author} from '~/src/types/meta';
import {VideoCmsItem, FeedContentSlideshow} from '~/src/types/feed/content';

const {publicRuntimeConfig: config} = getConfig();

interface StructuredArticleProps {
    datePublished: string;
    dateModified: string;
    authors: Author[];
    headline: string;
    description: string;
    images: Record<string, ImageVariant>;
    canonicalUrl: string;
    videos?: VideoCmsItem[];
    slideshows?: FeedContentSlideshow[];
}

const StructuredArticle = ({
    datePublished,
    dateModified,
    authors = [],
    headline,
    description,
    images,
    canonicalUrl,
    videos,
    slideshows
}: StructuredArticleProps) => {
    const author = authors.length
        ? authors.map((author) => ({
              '@type': 'Person',
              name: author.name
          }))
        : {'@type': 'NewsMediaOrganization', name: config.tenantName};

    const image =
        images &&
        ['fix1200x1200px', 'fix1200x900px', 'fix1200x675px']
            .filter((variant) => images[variant])
            .map((variant) => ({
                '@type': 'ImageObject',
                url: images[variant].src,
                width: images[variant].width,
                height: images[variant].height
            }));

    const video =
        videos &&
        videos.map((item) => ({
            '@type': 'VideoObject',
            name: item.oberzeile ? `${item.oberzeile}: ${item.title}` : item.title,
            description: item.lead,
            thumbnailUrl: item.thumbnail,
            uploadDate: utcToIso8601(normalizeDate(item.date)),
            duration: `PT${item.duration}S`,
            embedUrl: `${config.embedUrls.videoPlayer}?videoId=${item.video_id}&lang=${config.language}`
        }));

    const structuredSlideshows =
        slideshows?.map((slideshow) => ({
            '@type': 'Collection',
            image: slideshow.slides.map((slide) => ({
                '@type': 'ImageObject',
                ContentUrl: Object.values(slide.image.variants)
                    .filter((variant) => variant.maxWidth <= 1200 && variant.maxHeight <= 1200)
                    .sort((a, b) => b.maxWidth + b.maxHeight - (a.maxWidth + a.maxHeight))
                    .map((i) => i.src)[0],
                caption: [slide.image.captionTitle, slide.image.caption?.text].filter(Boolean).join(': '),
                creditText: slide.image.credit
            }))
        })) || [];

    const structuredData = {
        '@context': 'http://schema.org',
        '@type': 'NewsArticle',
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl
        },
        publisher: {
            '@type': 'Organization',
            name: config.tenantName,
            logo: {
                '@type': 'ImageObject',
                url: `${config.canonicalBaseUrl}${config.mainLogoPath}`,
                width: 144,
                height: 192
            }
        },
        headline,
        image,
        datePublished: utcToIso8601(datePublished),
        dateModified: utcToIso8601(dateModified),
        author,
        description,
        video,
        hasPart: [...structuredSlideshows]
    };

    return (
        <Head>
            <script key="structuredData" type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(structuredData)}}></script>
        </Head>
    );
};

export default StructuredArticle;
